import { useCallback, useEffect, useId, useRef } from "react";
import type { ReactNode } from "react";
import { cx } from "./utils";
import { IconButton } from "./IconButton";
import { Icon } from "./Icon";

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export interface SheetProps {
  /** Controls visibility; nothing renders when false. */
  open: boolean;
  /** Called on Esc, backdrop click, or the close button. */
  onClose: () => void;
  /** Dialog heading (also the accessible name). */
  title?: ReactNode;
  /** Footer row, typically the confirm/cancel buttons. */
  footer?: ReactNode;
  /** Desktop max width. @default "md" */
  size?: "sm" | "md" | "lg" | "full";
  /** Disable Esc / backdrop dismissal (destructive confirmations). */
  dismissible?: boolean;
  /** Hide the top-right close button. */
  hideClose?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * Modal surface: a bottom sheet below 768px, a centred dialog above.
 * Traps focus, restores it on close, closes on Esc and backdrop click.
 */
export function Sheet({
  open,
  onClose,
  title,
  footer,
  size = "md",
  dismissible = true,
  hideClose = false,
  className,
  children,
}: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<Element | null>(null);
  const titleId = useId();

  const close = useCallback(() => {
    if (dismissible) onClose();
  }, [dismissible, onClose]);

  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== "Tab" || !panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const firstEl = items[0]!;
      const lastEl = items[items.length - 1]!;
      if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      } else if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = prevOverflow;
      (restoreRef.current as HTMLElement | null)?.focus?.();
    };
  }, [open, close]);

  if (!open) return null;

  return (
    <div className="xz-sheet-root">
      <div className="xz-sheet__backdrop" onClick={close} data-testid="xz-sheet-backdrop" />
      <div
        ref={panelRef}
        className={cx("xz-sheet", `xz-sheet--${size}`, className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title != null ? titleId : undefined}
        tabIndex={-1}
      >
        {(title != null || !hideClose) && (
          <header className="xz-sheet__head">
            {title != null && (
              <h2 className="xz-sheet__title" id={titleId}>
                {title}
              </h2>
            )}
            {!hideClose && (
              <IconButton icon={<Icon id="ui-close" />} label="关闭" size="sm" onClick={onClose} />
            )}
          </header>
        )}
        <div className="xz-sheet__body">{children}</div>
        {footer != null && <footer className="xz-sheet__foot">{footer}</footer>}
      </div>
    </div>
  );
}
